import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import styles from './Popup.module.scss'
import { useBlockScroll } from 'hooks';

const Popup = ({
  open,
  onClose,
  children,
  anchorRef,
  id,
  position = 'bottom',
  minVisibleWidth=0, // new prop for media query style visibility
  maxVisibleWidth  // new prop for media query style visibility
}) => {
  const popupRef = useRef(null);
  const [style, setStyle] = useState({});
  const [isVisible, setIsVisible] = useState(true);

  // Media query visibility effect
  useEffect(() => {
    if (minVisibleWidth === undefined && maxVisibleWidth === undefined) {
      setIsVisible(true);
      return;
    }
    const checkVisibility = () => {
      const width = window.innerWidth;
      if (
        (minVisibleWidth !== undefined && width < minVisibleWidth) ||
        (maxVisibleWidth !== undefined && width > maxVisibleWidth)
      ) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    };
    checkVisibility();
    window.addEventListener('resize', checkVisibility);
    return () => window.removeEventListener('resize', checkVisibility);
  }, [minVisibleWidth, maxVisibleWidth]);

  useEffect(() => {
    if (open && anchorRef?.current && popupRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      let finalPosition = position;

      const calcPosition = (pos) => {
        switch (pos) {
          case 'top':
            return {
              top: anchorRect.top + window.scrollY - popupRect.height - 8,
              left: anchorRect.left + window.scrollX,
            };
          case 'left':
            return {
              top: anchorRect.top + window.scrollY,
              left: anchorRect.left + window.scrollX - popupRect.width - 8,
            };
          case 'right':
            return {
              top: anchorRect.top + window.scrollY,
              left: anchorRect.right + window.scrollX + 8,
            };
          case 'bottom':
          default:
            return {
              top: anchorRect.bottom + window.scrollY + 8,
              left: anchorRect.left + window.scrollX,
            };
        }
      };

      let posOrder = [position, 'bottom', 'top', 'right', 'left'];
      posOrder = posOrder.filter((v, i, a) => a.indexOf(v) === i);

      let coords;
      for (let pos of posOrder) {
        coords = calcPosition(pos);
        const fitsVertically =
          coords.top >= window.scrollY &&
          coords.top + popupRect.height <= window.scrollY + window.innerHeight;
        const fitsHorizontally =
          coords.left >= window.scrollX &&
          coords.left + popupRect.width <= window.scrollX + window.innerWidth;
        if (fitsVertically && fitsHorizontally) {
          finalPosition = pos;
          break;
        }
      }

      coords.top = Math.max(window.scrollY, Math.min(coords.top, window.scrollY + window.innerHeight - popupRect.height));
      coords.left = Math.max(window.scrollX, Math.min(coords.left, window.scrollX + window.innerWidth - popupRect.width));

      setStyle({
        position: 'absolute',
        top: coords.top,
        left: coords.left,
        zIndex: 1100,
      });
    }
  }, [open, anchorRef, position]);

  useEffect(() => {
    if (!open) return
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [open, onClose])

  useBlockScroll(open)

  if (!open || !isVisible) return null

  return ReactDOM.createPortal(
    <div>
      <div className={styles.overlay} onClick={onClose}>
      </div>
      <div
        ref={popupRef}
        className={styles.tooltipPopup}
        style={style}
        id={id}
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export default Popup
